// Sprint 8 T24 — Vista calendario semanal de un menu.
// T41: multi-plato por celda (plato suelto)
// T42: agrupacion por OPCIONES de menu. Cada celda puede tener N opciones
// (Menu 1, Menu 2, etc.) y cada opcion agrupa M platillos. El paciente
// elige una opcion completa.
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import type {
  AsignarPlatoItem,
  DietaResponse,
  DtpMenuItem,
  MenuOpcionResponse,
  MenuResponse,
  PlatoTemplateResponse,
} from '../../services/nutricionista'
import { nutricionistaApi } from '../../services/nutricionista'
import { ModalShell } from '../ui/ModalShell'

interface CalendarioMenuViewProps {
  menu: MenuResponse
  dietas: DietaResponse[]
  platos: PlatoTemplateResponse[]
  onConfirm: (items: AsignarPlatoItem[]) => Promise<void> | void
  onCancel: () => void
}

// IDs verificados contra la tabla tiempos (D=1, RAM=2, A=3, RPM=4, C=5, RNC=6)
const TIEMPOS = [
  { id: 1, codigo: 'D', nombre: 'Desayuno' },
  { id: 2, codigo: 'RAM', nombre: 'Refrigerio AM' },
  { id: 3, codigo: 'A', nombre: 'Almuerzo' },
  { id: 4, codigo: 'RPM', nombre: 'Refrigerio PM' },
  { id: 5, codigo: 'C', nombre: 'Cena' },
  { id: 6, codigo: 'RNC', nombre: 'Refrigerio Nocturno' },
]

const DIAS = [
  { id: 1, nombre: 'Lunes', corto: 'Lun' },
  { id: 2, nombre: 'Martes', corto: 'Mar' },
  { id: 3, nombre: 'Miércoles', corto: 'Mié' },
  { id: 4, nombre: 'Jueves', corto: 'Jue' },
  { id: 5, nombre: 'Viernes', corto: 'Vie' },
  { id: 6, nombre: 'Sábado', corto: 'Sáb' },
  { id: 7, nombre: 'Domingo', corto: 'Dom' },
]

type CellKey = string // `${tiempo_id}|${dia_semana}`

function cellKey(tiempo_id: number, dia: number): CellKey {
  return `${tiempo_id}|${dia}`
}

export default function CalendarioMenuView({
  menu,
  dietas,
  platos,
  onConfirm,
  onCancel,
}: CalendarioMenuViewProps) {
  const [dietaSel, setDietaSel] = useState<number | ''>(dietas[0]?.id ?? '')
  const [dtps, setDtps] = useState<DtpMenuItem[]>([])
  const [opciones, setOpciones] = useState<MenuOpcionResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [celdaEdit, setCeldaEdit] = useState<{ tiempo_id: number; dia: number } | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const labelId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    dialogRef.current?.focus()
  }, [])

  async function reload(dietaId: number) {
    setLoading(true)
    try {
      const [dtpData, opData] = await Promise.all([
        nutricionistaApi.listarDtpsMenu(menu.id, dietaId),
        nutricionistaApi.listarOpcionesMenu(menu.id, { dieta_id: dietaId }),
      ])
      setDtps(dtpData)
      setOpciones(opData)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error cargando datos del menu')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (dietaSel === '') {
      setDtps([])
      setOpciones([])
      return
    }
    reload(Number(dietaSel))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu.id, dietaSel])

  const platosAprobados = useMemo(
    () =>
      platos.filter(
        (p) =>
          String(p.estado).toUpperCase() === 'APROBADO' ||
          String(p.estado).toUpperCase() === 'VERIFICADO_CHEF',
      ),
    [platos],
  )

  const platoIndex = useMemo(() => {
    const m = new Map<number, PlatoTemplateResponse>()
    for (const p of platos) m.set(p.id, p)
    return m
  }, [platos])

  // Opciones agrupadas por celda
  const opcionesPorCelda = useMemo(() => {
    const m = new Map<CellKey, MenuOpcionResponse[]>()
    for (const op of opciones) {
      const k = cellKey(op.tiempo_id, op.dia_semana)
      const arr = m.get(k) ?? []
      arr.push(op)
      m.set(k, arr)
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => a.orden - b.orden)
    }
    return m
  }, [opciones])

  // DTPs indexados por menu_opcion_id
  const dtpsPorOpcion = useMemo(() => {
    const m = new Map<number, DtpMenuItem[]>()
    const sueltos: DtpMenuItem[] = [] // DTPs sin opcion (no debería pasar tras backfill)
    for (const d of dtps) {
      if (d.menu_opcion_id == null) {
        sueltos.push(d)
        continue
      }
      const arr = m.get(d.menu_opcion_id) ?? []
      arr.push(d)
      m.set(d.menu_opcion_id, arr)
    }
    for (const arr of m.values()) arr.sort((a, b) => a.orden_plato - b.orden_plato)
    return { porOpcion: m, sueltos }
  }, [dtps])

  function renderCelda(t: number, d: number) {
    const ops = opcionesPorCelda.get(cellKey(t, d)) ?? []
    if (ops.length === 0) {
      return <span className="text-gray-400 text-xs">—</span>
    }
    return (
      <div className="flex flex-col gap-1 text-xs">
        {ops.map((op) => {
          const platosOp = dtpsPorOpcion.porOpcion.get(op.id) ?? []
          return (
            <div key={op.id} className="bg-blue-50 dark:bg-gray-700 rounded px-1.5 py-1 border-l-2 border-avante-primary">
              {ops.length > 1 && (
                <div className="font-semibold text-[10px] truncate text-blue-700">{op.nombre}</div>
              )}
              <ul className="text-[11px] leading-snug text-gray-600 dark:text-gray-300 list-none">
                {platosOp.length === 0 ? (
                  <li className="italic text-gray-400">(vacía)</li>
                ) : (
                  platosOp.map((p) => (
                    <li key={p.id} className="truncate" title={p.plato_nombre ?? undefined}>
                      • {p.plato_nombre ?? p.plato_template_codigo ?? p.plato_codigo}
                    </li>
                  ))
                )}
              </ul>
            </div>
          )
        })}
      </div>
    )
  }

  // ----- mutaciones del mini-modal -----

  async function crearOpcionEnCelda(t: number, d: number, nombre: string) {
    if (dietaSel === '' || !nombre.trim()) return
    setError(null)
    try {
      const ops = opcionesPorCelda.get(cellKey(t, d)) ?? []
      const nextOrden = ops.reduce((m, o) => Math.max(m, o.orden), 0) + 1
      await nutricionistaApi.crearOpcionMenu(menu.id, {
        dieta_id: Number(dietaSel),
        tiempo_id: t,
        dia_semana: d,
        nombre: nombre.trim(),
        orden: nextOrden,
      })
      await reload(Number(dietaSel))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error creando opción')
    }
  }

  async function renombrarOpcion(opId: number, nombre: string) {
    if (!nombre.trim()) return
    setError(null)
    try {
      await nutricionistaApi.actualizarOpcionMenu(menu.id, opId, { nombre: nombre.trim() })
      if (dietaSel !== '') await reload(Number(dietaSel))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error renombrando opción')
    }
  }

  async function eliminarOpcion(opId: number) {
    if (!confirm('¿Eliminar esta opción? Los platos asignados se quedarán sin opción.')) return
    setError(null)
    try {
      await nutricionistaApi.eliminarOpcionMenu(menu.id, opId)
      if (dietaSel !== '') await reload(Number(dietaSel))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error eliminando opción')
    }
  }

  async function agregarPlatoEnOpcion(opcion: MenuOpcionResponse, platoTemplateId: number) {
    if (dietaSel === '') return
    setError(null)
    setSubmitting(true)
    try {
      const platosOp = dtpsPorOpcion.porOpcion.get(opcion.id) ?? []
      const nextOrden = platosOp.reduce((m, p) => Math.max(m, p.orden_plato), 0) + 1
      const items: AsignarPlatoItem[] = [{
        dieta_id: Number(dietaSel),
        tiempo_id: opcion.tiempo_id,
        dia_semana: opcion.dia_semana,
        plato_id: platoTemplateId,
        orden_plato: nextOrden,
        menu_opcion_id: opcion.id,
      }]
      await onConfirm(items)
      await reload(Number(dietaSel))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error agregando plato')
    } finally {
      setSubmitting(false)
    }
  }

  async function quitarPlatoDeOpcion(dtp: DtpMenuItem) {
    if (!confirm('¿Quitar este plato de la opción?')) return
    setError(null)
    setSubmitting(true)
    try {
      await nutricionistaApi.eliminarDtpMenu(menu.id, dtp.id)
      if (dietaSel !== '') await reload(Number(dietaSel))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error quitando plato')
    } finally {
      setSubmitting(false)
    }
  }

  const dietaActual = dietas.find((d) => d.id === Number(dietaSel))

  return (
    // Hospital-Tech (4-jul): ventana de trabajo grande — NO usa ModalShell
    // (mantiene su ancho 6xl), pero comparte backdrop opaco + blur y panel
    // 100% solido con sombra fuerte.
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
      style={{ background: 'rgba(10,10,25,.72)', backdropFilter: 'blur(4px)' }}
      onClick={() => !submitting && onCancel()}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelId}
        tabIndex={-1}
        className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-6xl w-full my-8"
        style={{ boxShadow: '0 40px 90px -20px rgba(0,0,0,.7)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex justify-between items-start mb-4">
          <div>
            <h2 id={labelId} className="font-bold text-lg text-gray-900 dark:text-gray-50">
              Calendario semanal — {menu.codigo} {menu.nombre}
            </h2>
            <p className="text-xs text-gray-500">
              Vigencia: {menu.fecha_vigencia_inicio}
              {menu.fecha_vigencia_fin ? ` → ${menu.fecha_vigencia_fin}` : ''} · Estado:{' '}
              <span className="uppercase font-semibold">{menu.estado}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            aria-label="Cerrar"
            className="text-gray-500 hover:text-gray-700 text-xl"
          >
            ×
          </button>
        </header>

        <div className="mb-4">
          <label className="block">
            <span className="text-sm font-semibold">Dieta</span>
            <select
              value={dietaSel}
              onChange={(e) => setDietaSel(e.target.value === '' ? '' : Number(e.target.value))}
              aria-label="Seleccionar dieta"
              disabled={submitting}
              className="mt-1 w-full md:w-1/2 border rounded px-2 py-1 text-sm bg-white"
            >
              {dietas.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.codigo} — {d.nombre}
                </option>
              ))}
            </select>
          </label>
          {dietaActual && (
            <p className="text-xs text-gray-500 mt-1">
              {dietaActual.calorias ? `${dietaActual.calorias} kcal · ` : ''}
              CHO {dietaActual.cho_pct ?? '?'}% · Prot {dietaActual.prot_pct ?? '?'}% · Grasa{' '}
              {dietaActual.grasa_pct ?? '?'}%
            </p>
          )}
        </div>

        {loading ? (
          <p className="text-sm text-gray-500 text-center py-8">Cargando matriz...</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700">
                  <th className="border p-2 text-left font-semibold w-24">Tiempo</th>
                  {DIAS.map((d) => (
                    <th key={d.id} className="border p-2 text-center font-semibold" scope="col">
                      <span className="hidden md:inline">{d.nombre}</span>
                      <span className="md:hidden">{d.corto}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {TIEMPOS.map((t) => (
                  <tr key={t.id}>
                    <th scope="row" className="border p-2 text-left font-semibold bg-gray-50 dark:bg-gray-700">
                      {t.nombre}
                    </th>
                    {DIAS.map((d) => (
                      <td
                        key={d.id}
                        className="border p-1 text-left align-top hover:bg-blue-50 dark:hover:bg-gray-700 cursor-pointer min-w-[140px] max-w-[180px]"
                        onClick={() => setCeldaEdit({ tiempo_id: t.id, dia: d.id })}
                        title="Click para administrar opciones y platos"
                      >
                        {renderCelda(t.id, d.id)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-xs text-gray-500 mt-2">
          Tip: cada celda puede tener varias opciones de menú (Op1, Op2...). Cada opción agrupa
          platos. Click en una celda para administrar.
        </p>

        {error && (
          <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2 mt-3">
            {error}
          </p>
        )}

        <footer className="flex gap-2 pt-4 border-t mt-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="flex-1 border py-2 rounded text-sm disabled:opacity-50"
          >
            Cerrar
          </button>
        </footer>

        {celdaEdit && (
          <CeldaEditor
            menuId={menu.id}
            tiempo_id={celdaEdit.tiempo_id}
            dia={celdaEdit.dia}
            opciones={opcionesPorCelda.get(cellKey(celdaEdit.tiempo_id, celdaEdit.dia)) ?? []}
            dtpsPorOpcion={dtpsPorOpcion.porOpcion}
            platoIndex={platoIndex}
            platosAprobados={platosAprobados}
            tiempoLabel={TIEMPOS.find((t) => t.id === celdaEdit.tiempo_id)?.nombre ?? ''}
            diaLabel={DIAS.find((d) => d.id === celdaEdit.dia)?.nombre ?? ''}
            submitting={submitting}
            onClose={() => setCeldaEdit(null)}
            onCrearOpcion={(nombre) => crearOpcionEnCelda(celdaEdit.tiempo_id, celdaEdit.dia, nombre)}
            onRenombrarOpcion={renombrarOpcion}
            onEliminarOpcion={eliminarOpcion}
            onAgregarPlato={agregarPlatoEnOpcion}
            onQuitarPlato={quitarPlatoDeOpcion}
          />
        )}
      </div>
    </div>
  )
}

// ============================================================
// Mini-modal de edición de celda
// ============================================================
interface CeldaEditorProps {
  menuId: number
  tiempo_id: number
  dia: number
  opciones: MenuOpcionResponse[]
  dtpsPorOpcion: Map<number, DtpMenuItem[]>
  platoIndex: Map<number, PlatoTemplateResponse>
  platosAprobados: PlatoTemplateResponse[]
  tiempoLabel: string
  diaLabel: string
  submitting: boolean
  onClose: () => void
  onCrearOpcion: (nombre: string) => Promise<void> | void
  onRenombrarOpcion: (opId: number, nombre: string) => Promise<void> | void
  onEliminarOpcion: (opId: number) => Promise<void> | void
  onAgregarPlato: (opcion: MenuOpcionResponse, platoId: number) => Promise<void> | void
  onQuitarPlato: (dtp: DtpMenuItem) => Promise<void> | void
}

function CeldaEditor({
  opciones,
  dtpsPorOpcion,
  platosAprobados,
  tiempoLabel,
  diaLabel,
  submitting,
  onClose,
  onCrearOpcion,
  onRenombrarOpcion,
  onEliminarOpcion,
  onAgregarPlato,
  onQuitarPlato,
}: CeldaEditorProps) {
  const [nuevoNombre, setNuevoNombre] = useState('')
  const [opcionActiva, setOpcionActiva] = useState<number | null>(opciones[0]?.id ?? null)

  useEffect(() => {
    // Si la opcion activa fue eliminada o no existe, ir a la primera
    if (opcionActiva && !opciones.find((o) => o.id === opcionActiva)) {
      setOpcionActiva(opciones[0]?.id ?? null)
    } else if (!opcionActiva && opciones.length > 0) {
      setOpcionActiva(opciones[0].id)
    }
  }, [opciones, opcionActiva])

  const opActual = opciones.find((o) => o.id === opcionActiva) ?? null
  const platosOp = opActual ? (dtpsPorOpcion.get(opActual.id) ?? []) : []

  return (
    <ModalShell
      zIndex={60}
      maxWidth={560}
      title={`${tiempoLabel} · ${diaLabel}`}
      subtitle="Administra las opciones de menú de esta celda. Cada opción es un combo de platillos que el paciente puede elegir."
      ariaLabel="Editar opciones y platos de la celda"
      onClose={onClose}
    >
        {/* Tabs de opciones */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {opciones.map((op) => (
            <button
              key={op.id}
              type="button"
              onClick={() => setOpcionActiva(op.id)}
              className="text-xs px-3 py-1.5 rounded-lg font-semibold"
              style={
                op.id === opcionActiva
                  ? { background: '#006EB4', color: '#fff' }
                  : {
                      background: 'rgba(255,255,255,.06)',
                      color: '#94A0CD',
                      border: '1px solid rgba(140,170,230,.15)',
                    }
              }
            >
              {op.nombre}
            </button>
          ))}
        </div>

        {/* Crear nueva opcion */}
        <div
          className="mb-4 flex gap-2 rounded-xl p-3"
          style={{
            background: 'rgba(255,255,255,.04)',
            border: '1px solid rgba(140,170,230,.15)',
          }}
        >
          <input
            type="text"
            value={nuevoNombre}
            onChange={(e) => setNuevoNombre(e.target.value)}
            placeholder="Nombre de nueva opción (ej. Tradicional, Líquido)"
            className="flex-1 rounded-lg px-3 py-2 text-sm"
            style={{
              background: 'rgba(255,255,255,.06)',
              border: '1px solid rgba(140,170,230,.22)',
              color: '#EAF2FF',
            }}
            maxLength={80}
          />
          <button
            type="button"
            onClick={() => {
              if (nuevoNombre.trim()) {
                onCrearOpcion(nuevoNombre)
                setNuevoNombre('')
              }
            }}
            disabled={submitting || !nuevoNombre.trim()}
            className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
            style={{ background: '#006EB4', color: '#fff' }}
          >
            + Opción
          </button>
        </div>

        {/* Contenido de la opcion activa */}
        {opActual ? (
          <div
            className="rounded-xl p-4"
            style={{
              background: 'rgba(255,255,255,.04)',
              border: '1px solid rgba(140,170,230,.15)',
            }}
          >
            <div className="flex items-center gap-2 mb-3">
              <input
                type="text"
                defaultValue={opActual.nombre}
                onBlur={(e) => {
                  const v = e.target.value.trim()
                  if (v && v !== opActual.nombre) onRenombrarOpcion(opActual.id, v)
                }}
                className="flex-1 rounded-lg px-3 py-2 text-sm font-semibold"
                style={{
                  background: 'rgba(255,255,255,.06)',
                  border: '1px solid rgba(140,170,230,.22)',
                  color: '#EAF2FF',
                }}
                maxLength={80}
                title="Click para renombrar la opción"
              />
              <button
                type="button"
                onClick={() => onEliminarOpcion(opActual.id)}
                disabled={submitting}
                className="text-xs font-semibold hover:underline"
                style={{ color: '#FF5C7A' }}
                title="Eliminar opción"
              >
                Eliminar
              </button>
            </div>

            <p className="text-xs font-semibold mb-2" style={{ color: '#94A0CD' }}>
              Platos en esta opción:
            </p>
            {platosOp.length === 0 ? (
              <p className="text-xs italic mb-2" style={{ color: '#94A0CD' }}>
                Sin platos asignados
              </p>
            ) : (
              <ul className="space-y-1.5 mb-4">
                {platosOp.map((p) => (
                  <li
                    key={p.id}
                    className="flex items-center gap-2 text-sm rounded-lg px-3 py-1.5"
                    style={{
                      background: 'rgba(255,255,255,.04)',
                      border: '1px solid rgba(140,170,230,.10)',
                    }}
                  >
                    <span
                      className="flex-1"
                      style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}
                    >
                      <span style={{ color: '#7FD4FF' }}>
                        {p.plato_template_codigo ?? p.plato_codigo}
                      </span>{' '}
                      — {p.plato_nombre}
                    </span>
                    <button
                      type="button"
                      onClick={() => onQuitarPlato(p)}
                      disabled={submitting}
                      className="text-base leading-none"
                      style={{ color: '#FF5C7A' }}
                      title="Quitar plato"
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <p className="text-xs font-semibold mb-2" style={{ color: '#94A0CD' }}>
              + Agregar plato:
            </p>
            {platosAprobados.length === 0 ? (
              <p className="text-xs" style={{ color: '#F5A623' }}>
                No hay platos APROBADOS disponibles.
              </p>
            ) : (
              <div
                className="max-h-40 overflow-y-auto rounded-lg"
                style={{ border: '1px solid rgba(140,170,230,.15)' }}
              >
                {platosAprobados.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => onAgregarPlato(opActual, p.id)}
                    disabled={submitting}
                    className="w-full text-left px-3 py-1.5 text-xs disabled:opacity-50"
                    style={{
                      color: '#EAF2FF',
                      borderBottom: '1px solid rgba(140,170,230,.08)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(0,110,180,.25)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent'
                    }}
                  >
                    <span
                      style={{ fontFamily: "'JetBrains Mono', monospace", color: '#7FD4FF' }}
                    >
                      {p.codigo}
                    </span>{' '}
                    — {p.nombre_compuesto}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm italic text-center py-4" style={{ color: '#94A0CD' }}>
            Sin opciones. Crea la primera con el campo de arriba.
          </p>
        )}

        <div className="pt-4 mt-4" style={{ borderTop: '1px solid rgba(140,170,230,.15)' }}>
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2.5 rounded-xl text-sm font-semibold"
            style={{ background: '#006EB4', color: '#fff' }}
          >
            Listo
          </button>
        </div>
    </ModalShell>
  )
}
